import React, { useRef, useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, EffectFade } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/effect-fade";
import axios from "axios";
import config from "../config.json";

//import slide1 from "../assets/img/slide1.jpg";
import churchMain from "../assets/img/church.jpg";
///import slide2 from "../assets/img/slide2.jpg";
import church1 from "../assets/img/church/church1.jpg";
import church2 from "../assets/img/church/church2.jpg";
import church3 from "../assets/img/church/church3.jpg";

export default function Slider() {
  const swiperRef = useRef(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  // High-res authentic church default slides
  const defaultSlides = [
    {
      src: churchMain,
      title: "St. Mary's Cathedral",
      subtitle: "Mother Church of the Roman Catholic Archdiocese of Madurai",
      caption: "A historic beacon of faith, prayer, and Catholic heritage established in 1841."
    },
    {
      src: churchMain,
      title: "Majestic Bell Towers",
      subtitle: "42-Meter Towers & Romanesque Architecture",
      caption: "An architectural marvel blending European, Roman, and Continental styles."
    },
    {
      src: churchMain,
      title: "A Vibrant Catholic Community",
      subtitle: "Over 1,600 Families & 14 Substations",
      caption: "Dedicated to prayer, spiritual renewal, and active service across Madurai."
    },
    {
      src: church1,
      title: "Solemn Liturgies & Feasts",
      subtitle: "Daily Eucharistic Celebrations",
      caption: "Gathering the faithful for daily Mass and traditional Catholic devotions."
    },
    {
      src: church2,
      title: "Sanctuary of Grace",
      subtitle: "Under the Patronage of Our Lady",
      caption: "Drawing pilgrims and visitors into peaceful prayer and contemplation."
    },
    {
      src: church3,
      title: "Historic Edifice",
      subtitle: "Over 180 Years of Continuous Witness",
      caption: "Preserving rich sacred history from Fr. Bertrand SJ to the present day."
    }
  ];

  const [slides, setSlides] = useState(defaultSlides);

  useEffect(() => {
    fetchSliderImages();
  }, []);

  const fetchSliderImages = async () => {
    if (!config?.api?.invokeUrl) return;
    try {
      const fileName = "images/slider/";
      const bucketName = "images.stmaryscathedral.in";
      const response = await axios.get(
        `${config.api.invokeUrl}/website/gallery/slider?fileName=${fileName}&bucketName=${bucketName}`,
        {
          headers: {
            "x-api-key": config.api.key || ""
          }
        }
      );
      if (Array.isArray(response.data) && response.data.length > 0) {
        const remoteSlides = response.data.map((item, idx) => ({
          src: `${config.s3?.imageUrl || ""}${item}`,
          title: "St. Mary's Cathedral",
          subtitle: `Archdiocese of Madurai • View ${idx + 1}`,
          caption: "A sacred house of prayer in the heart of Madurai."
        }));
        setSlides(remoteSlides);
      }
    } catch (error) {
      // Gracefully retain default authentic church photography
      console.log("Using local cathedral photography for hero slideshow.");
    }
  };

  const handleNext = () => {
    if (swiperRef.current) swiperRef.current.slideNext();
  };

  const handlePrev = () => {
    if (swiperRef.current) swiperRef.current.slidePrev();
  };

  const handleSlideClick = (index) => {
    setActiveIndex(index);
    setFullscreen(true);
  };

  const handleCloseFullscreen = () => setFullscreen(false);

  return (
    <div className="cathedral-slider-container" style={{ position: "relative", width: "100%", borderRadius: "18px", overflow: "hidden", boxShadow: "0 10px 36px rgba(27, 58, 107, 0.12)", border: "1px solid #e9e4dc", background: "#f8f6f0" }}>
      <Swiper
        modules={[Autoplay, Navigation, EffectFade]}
        effect="fade"
        loop
        autoplay={{ delay: 5000, disableOnInteraction: false }}
        className="w-full h-full"
        onSwiper={(swiper) => { swiperRef.current = swiper; }}
        onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
      >
        {slides.map((slide, i) => (
          <SwiperSlide key={i}>
            <div
              className="cathedral-slide"
              style={{
                position: "relative",
                width: "100%",
                height: "560px",
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "flex-start",
                cursor: "pointer",
                overflow: "hidden"
              }}
              onClick={() => handleSlideClick(i)}
            >
              <img
                src={slide.src}
                alt={slide.title}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "center"
                }}
              />

              {/* Dignified Subtle Gradient Overlay */}
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: "linear-gradient(to top, rgba(16, 37, 68, 0.85) 0%, rgba(16, 37, 68, 0.4) 40%, rgba(0, 0, 0, 0.1) 100%)"
                }}
              />

              {/* Slide Content */}
              <div
                className="slide-content p-4 p-md-5 text-white"
                style={{
                  position: "relative",
                  zIndex: 2,
                  maxWidth: "780px"
                }}
              >
                <span
                  className="badge mb-2"
                  style={{
                    backgroundColor: "rgba(184, 149, 71, 0.9)",
                    color: "#ffffff",
                    fontFamily: "Cinzel, serif",
                    letterSpacing: "1.5px",
                    fontSize: "0.78rem",
                    padding: "6px 14px",
                    borderRadius: "4px"
                  }}
                >
                  {slide.subtitle}
                </span>
                <h2
                  style={{
                    fontFamily: "'Playfair Display', Georgia, serif",
                    fontSize: "clamp(1.75rem, 3.5vw, 2.75rem)",
                    fontWeight: 700,
                    color: "#ffffff",
                    textShadow: "0 2px 10px rgba(0,0,0,0.5)",
                    marginBottom: "10px",
                    lineHeight: 1.2
                  }}
                >
                  {slide.title}
                </h2>
                <p
                  className="d-none d-sm-block mb-0"
                  style={{
                    fontSize: "1.05rem",
                    color: "#f1ede6",
                    textShadow: "0 1px 4px rgba(0,0,0,0.5)",
                    maxWidth: "600px",
                    lineHeight: 1.6
                  }}
                >
                  {slide.caption}
                </p>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Elegant Nav Arrows */}
      <button
        onClick={handlePrev}
        aria-label="Previous Slide"
        style={{
          position: "absolute",
          top: "50%",
          left: "20px",
          transform: "translateY(-50%)",
          zIndex: 10,
          background: "rgba(255, 255, 255, 0.85)",
          border: "1px solid rgba(184, 149, 71, 0.4)",
          borderRadius: "50%",
          width: "44px",
          height: "44px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#1b3a6b",
          boxShadow: "0 4px 14px rgba(0, 0, 0, 0.15)",
          cursor: "pointer",
          transition: "all 0.25s ease"
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.background = "#1b3a6b";
          e.currentTarget.style.color = "#ffffff";
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.background = "rgba(255, 255, 255, 0.85)";
          e.currentTarget.style.color = "#1b3a6b";
        }}
      >
        <i className="fas fa-chevron-left"></i>
      </button>

      <button
        onClick={handleNext}
        aria-label="Next Slide"
        style={{
          position: "absolute",
          top: "50%",
          right: "20px",
          transform: "translateY(-50%)",
          zIndex: 10,
          background: "rgba(255, 255, 255, 0.85)",
          border: "1px solid rgba(184, 149, 71, 0.4)",
          borderRadius: "50%",
          width: "44px",
          height: "44px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#1b3a6b",
          boxShadow: "0 4px 14px rgba(0, 0, 0, 0.15)",
          cursor: "pointer",
          transition: "all 0.25s ease"
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.background = "#1b3a6b";
          e.currentTarget.style.color = "#ffffff";
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.background = "rgba(255, 255, 255, 0.85)";
          e.currentTarget.style.color = "#1b3a6b";
        }}
      >
        <i className="fas fa-chevron-right"></i>
      </button>

      {/* Fullscreen Lightbox */}
      {fullscreen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(10, 20, 36, 0.95)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px"
          }}
          onClick={handleCloseFullscreen}
        >
          <img
            src={slides[activeIndex]?.src}
            alt={slides[activeIndex]?.title || "Cathedral Photography"}
            style={{
              maxWidth: "92vw",
              maxHeight: "88vh",
              borderRadius: "12px",
              boxShadow: "0 12px 40px rgba(0,0,0,0.6)",
              objectFit: "contain"
            }}
          />
          <button
            onClick={handleCloseFullscreen}
            aria-label="Close Fullscreen"
            style={{
              position: "absolute",
              top: "24px",
              right: "24px",
              fontSize: "1.75rem",
              color: "#ffffff",
              background: "rgba(255,255,255,0.15)",
              border: "none",
              borderRadius: "50%",
              width: "44px",
              height: "44px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer"
            }}
          >
            &#10005;
          </button>
        </div>
      )}
    </div>
  );
}
